import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Divider, Stack } from "@mui/material";

import { ControlledAutocomplete, FieldControl } from "../../../shared/components";
import { useGetUsers } from "../../../shared/hooks";
import { useDisplayField } from "../../../hooks/useDisplayField";
import { useStageGate } from "../../projectOverview/hooks/useStageGate";
import { useAddArtefacts, useEditArtefacts, useFetchArtefactsDetails } from "../mutation";
import { AutocompleteOption } from "../../../shared/components/ControlledAutocomplete";

export const CreateArtefacts: React.FC<{ projectId: string, isEdit: boolean, artefactId: string | null, onClose: () => void, onSave: () => void }> = ({ projectId, isEdit, artefactId, onClose, onSave }) => {

    const addArtefacts = useAddArtefacts();
    const editArtefacts = useEditArtefacts();

    const { data: artefactDetails, isLoading: isPending, removeCache } = useFetchArtefactsDetails(artefactId!);

    const [artefact, setArtefact] = useState<{
        title: null | string;
        category: null | string;
        sub_category: null | string;
        description: null | string;
        stage_gate: null | string;
        assigned_to: null | string;
        required_date: null | string;
        compliance: boolean;
        checklist_id: null | any;
        project: AutocompleteOption | null;
    }>({
        title: null,
        category: null,
        sub_category: null,
        description: null,
        stage_gate: null,
        assigned_to: null,
        required_date: null,
        compliance: false,
        checklist_id: null,
        project: null
    });

    useEffect(() => {
        if (isEdit && artefactDetails) {
            setArtefact({
                title: artefactDetails.title,
                category: artefactDetails.category,
                sub_category: artefactDetails.sub_category,
                description: artefactDetails.description,
                stage_gate: artefactDetails.stage_gate_id,
                assigned_to: artefactDetails.assigned_to,
                required_date: artefactDetails.required_date,
                compliance: artefactDetails.compliance === "1" ? true : false,
                checklist_id: artefactDetails.checklist_id,
                project: artefactDetails.project_id && artefactDetails.project_title ? {
                    id: artefactDetails.project_id,
                    title: artefactDetails.project_title
                } : null
            });
        }
    }, [isEdit, artefactDetails, isPending]);

    const { data: usersData, isLoading: isLoadingUsers } = useGetUsers();
    const userOptions = useMemo(() => {
        if (!usersData?.data) return [];
        return usersData.data
            .filter((u) => u.is_active === "1")
            .map((u) => ({
                id: u.user_id,
                name: `${u.first_name} ${u.last_name}`,
            }));
    }, [usersData]);

    const { stageGateData, isFetching } = useStageGate(String(artefact.project?.id || projectId));

    const onChange = (key: string, value: string | boolean | AutocompleteOption | AutocompleteOption[] | null) => {
        setArtefact({
            ...artefact,
            [key]: value
        });
    };

    const { getFilterOptions } = useDisplayField([]);

    const payload = () => {
        return {
            "title": artefact.title,
            "category": artefact.category,
            "sub_category": artefact.sub_category,
            "description": artefact.description,
            "assigned_to": artefact.assigned_to,
            "compliance": artefact.compliance,
            "required_date": artefact.required_date,
            "stage_gate_id": artefact.stage_gate,
            "checklist_id": artefact.checklist_id,
            "project_id": artefact.project?.id || null
        }
    };

    const onSubmit = async () => {
        if (isEdit && artefactId) {
            await editArtefacts.mutateAsync({ payload: payload(), artefactId: String(artefactId) });
        } else {
            await addArtefacts.mutateAsync({ payload: payload() });
        };
        removeCache();
        onSave();
        onClose();
    };

    if (isEdit && isPending) {
        return (
            <Box
                height="100%"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                width="100%"
                gap={2}
            >
                <CircularProgress />
            </Box>
        )
    }

    return (
        <Box display={'flex'} flexDirection={'column'} gap={2} height={'100%'}>

            <Box display={'flex'} flexDirection={'column'} gap={2} flex={1} p={2}>

                <FieldControl
                    keyName={'title'}
                    label={"Artefact Title"}
                    variant={"input"}
                    value={artefact.title}
                    onChange={onChange}
                />

                <FieldControl
                    keyName={'description'}
                    label={"Description"}
                    variant={"input"}
                    value={artefact.description}
                    onChange={onChange}
                />

                <ControlledAutocomplete
                    type="PROJECT"
                    value={artefact.project}
                    onSelect={(values) => onChange("project", values)}
                    label="Search project"
                    placeholder="Search project"
                />

                <FieldControl
                    keyName={'category'}
                    label={"Artefact Category"}
                    value={artefact.category}
                    variant={"select"}
                    options={getFilterOptions('ARTEFACT_CATEGORY').map((value) => {
                        return {
                            label: value.display_text,
                            value: value.key
                        }
                    })}
                    onChange={onChange}
                />

                <FieldControl
                    keyName={'sub_category'}
                    label={"Artefact Sub-Category"}
                    value={artefact.sub_category}
                    variant={"select"}
                    options={getFilterOptions('ARTEFACT_SUB_CATEGORY').map((value) => {
                        return {
                            label: value.display_text,
                            value: value.key
                        }
                    })}
                    onChange={onChange}
                />

                <FieldControl
                    keyName={'stage_gate'}
                    label={"Select Stage Gate"}
                    variant={"select"}
                    value={artefact.stage_gate}
                    loading={isFetching}
                    options={
                        stageGateData?.map((value: { id: string, name: string; }) => {
                            return {
                                value: value.id, label: value.name
                            }
                        })
                    }
                    onChange={onChange}
                />

                <FieldControl
                    keyName={'assigned_to'}
                    label={"Assigned to"}
                    variant={"select"}
                    options={userOptions.map((u) => ({
                        value: u.id,
                        label: u.name,
                    }))}
                    loading={isLoadingUsers}
                    value={artefact.assigned_to}
                    onChange={onChange}
                />

                <FieldControl
                    keyName={'required_date'}
                    label={"Required Date"}
                    variant={"datetime"}
                    value={artefact.required_date}
                    onChange={onChange}
                />

            </Box>

            <Stack direction="row" spacing={2} justifyContent="flex-end" flexShrink={0} bgcolor={'action.hover'} p={2}>
                <Button variant="outlined" onClick={onClose} loading={addArtefacts.isPending || editArtefacts.isPending}>Close</Button>
                <Button variant="contained" color="primary" onClick={onSubmit} loading={addArtefacts.isPending || editArtefacts.isPending}>{isEdit ? 'Save Changes' : 'Create Artefact'}</Button>
            </Stack>

        </Box>
    );
};